import type {
  ConsentState,
  CustomerField,
  CustomerRow,
  CustomerSort,
  CustomerUnavailableReason,
} from '@/api/customers'
import { Badge, formatInteger, type BadgeTone } from '@/design'

import { day, money, nullableCells, UNDEFINED, type ListCellContext } from '../list/cells'
import type { DescribeReason, ListColumn, ReasonRule } from '../list/nullState'

/** What a column's cells need beyond the row: the shared list context. */
export type CellContext = ListCellContext<CustomerRow, CustomerUnavailableReason>

export type ColumnGroup = 'identity' | 'lifetime' | 'range' | 'record'

export const GROUP_LABELS: Record<ColumnGroup, string> = {
  identity: 'Customer',
  lifetime: 'Lifetime',
  range: 'This period',
  record: 'Website record',
}

export interface CustomerColumn extends ListColumn<CustomerRow, CellContext> {
  group: ColumnGroup
  field?: CustomerField
  sort?: CustomerSort
}

/** Why a column is empty, in the words the page's note uses. */
export const REASON_TEXT: Record<CustomerUnavailableReason, string> = {
  customer_not_synced:
    'The customer’s own record has not synced from the website yet, so they are known only from their orders. Their record columns fill in once it syncs.',
  no_segments_yet: 'No customer segments are stored yet.',
}

/** What a missing cell says for each reason. */
const REASON_LABEL: Record<CustomerUnavailableReason, string> = {
  customer_not_synced: 'Not synced yet',
  no_segments_yet: 'Not live yet',
}

/**
 * The one rule for a customer's null: a record column is missing on an unsynced row
 * only (on a synced row its null is undefined: no touch credited, say), and segments are
 * missing on every row.
 */
export const unavailableReason: ReasonRule<CustomerRow, CustomerUnavailableReason> = (row, field, notAvailable) => {
  const reason = notAvailable[field]
  if (!reason) return null
  if (reason === 'customer_not_synced') return row.synced ? null : reason
  return reason
}

/** How the customers list words a reason: a missing record is "Not synced yet", segments "Not live yet". */
export const describeReason: DescribeReason<CustomerUnavailableReason> = (reason) => ({
  label: REASON_LABEL[reason],
  why: REASON_TEXT[reason],
})

const { figure, missing } = nullableCells<CustomerRow, CustomerUnavailableReason, CellContext>(unavailableReason, describeReason)

const count = (value: number) => formatInteger(value)

const CONSENT_LABELS: Record<ConsentState, string> = {
  granted: 'Granted',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
  unknown: 'Unknown',
}

const CONSENT_TONES: Record<ConsentState, BadgeTone> = {
  granted: 'positive',
  denied: 'neutral',
  withdrawn: 'warning',
  unknown: 'neutral',
}

/** A consent state as the page names it; a state a later API adds reads as itself. */
export function consentLabel(state: string): string {
  return Object.hasOwn(CONSENT_LABELS, state) ? CONSENT_LABELS[state as ConsentState] : state
}

function ConsentBadge({ state }: { state: ConsentState }) {
  return <Badge tone={Object.hasOwn(CONSENT_TONES, state) ? CONSENT_TONES[state] : 'neutral'}>{consentLabel(state)}</Badge>
}

function membership(isMember: boolean, isVip: boolean | null): string {
  const flags = [isMember ? 'Member' : null, isVip ? 'VIP' : null].filter(Boolean)
  return flags.length > 0 ? flags.join(' · ') : 'Neither'
}

/** Every column the list can show, in display order. The first is always shown. */
export const CUSTOMER_COLUMNS: readonly CustomerColumn[] = [
  {
    key: 'customer_ref',
    label: 'Customer',
    group: 'identity',
    byDefault: true,
    // The masked reference as served; the page adds the reveal (`CustomersPage`'s `cells`).
    cell: (row) => row.customerRef,
  },
  {
    key: 'last_purchase_at',
    label: 'Last purchase',
    group: 'lifetime',
    sort: 'last_purchase_at',
    byDefault: true,
    cell: figure('lastPurchaseAt', (row) => row.lastPurchaseAt, day),
  },
  {
    key: 'orders',
    label: 'Orders',
    group: 'lifetime',
    sort: 'orders',
    byDefault: true,
    align: 'end',
    cell: (row) => count(row.orders),
  },
  {
    key: 'tickets',
    label: 'Tickets',
    group: 'lifetime',
    sort: 'tickets',
    byDefault: true,
    align: 'end',
    cell: (row) => count(row.tickets),
  },
  {
    key: 'lifetime_revenue',
    label: 'Lifetime revenue',
    group: 'lifetime',
    sort: 'lifetime_revenue',
    byDefault: true,
    align: 'end',
    cell: (row, context) => money(row.lifetimeRevenueMinor, context),
  },
  {
    key: 'aov',
    label: 'Average order',
    group: 'lifetime',
    sort: 'aov',
    byDefault: true,
    align: 'end',
    cell: figure('aovMinor', (row) => row.aovMinor, money),
  },
  {
    key: 'first_touch',
    label: 'First-touch source',
    group: 'record',
    field: 'firstTouch',
    byDefault: true,
    cell: figure(
      'firstTouch',
      (row) => row.firstTouch,
      (touch) => {
        const source = [touch.source, touch.medium].filter(Boolean).join(' / ')
        return (
          <span className="list-cell-stack">
            <span>{source || 'Unnamed source'}</span>
            {touch.campaign ? <span className="list-cell-sub">{touch.campaign}</span> : null}
          </span>
        )
      },
    ),
  },
  {
    key: 'membership',
    label: 'Member / VIP',
    group: 'record',
    field: 'isMember',
    byDefault: true,
    cell: figure(
      'isMember',
      (row) => (row.isMember === null ? null : { isMember: row.isMember, isVip: row.isVip }),
      ({ isMember, isVip }) => membership(isMember, isVip),
    ),
  },
  {
    key: 'consent',
    label: 'Marketing consent',
    group: 'record',
    field: 'consent',
    byDefault: true,
    cell: figure('consent', (row) => row.consent, (consent) => <ConsentBadge state={consent.marketingState} />),
  },
  {
    key: 'first_purchase_at',
    label: 'First purchase',
    group: 'lifetime',
    sort: 'first_purchase_at',
    byDefault: false,
    cell: figure('firstPurchaseAt', (row) => row.firstPurchaseAt, day),
  },
  {
    key: 'refunds',
    label: 'Refunds',
    group: 'lifetime',
    sort: 'refunds',
    byDefault: false,
    align: 'end',
    cell: (row, context) => money(row.refundsMinor, context),
  },
  {
    key: 'range_orders',
    label: 'Orders in period',
    group: 'range',
    sort: 'range_orders',
    byDefault: false,
    align: 'end',
    cell: (row) => count(row.rangeOrders),
  },
  {
    key: 'range_tickets',
    label: 'Tickets in period',
    group: 'range',
    sort: 'range_tickets',
    byDefault: false,
    align: 'end',
    cell: (row) => count(row.rangeTickets),
  },
  {
    key: 'range_revenue',
    label: 'Revenue in period',
    group: 'range',
    sort: 'range_revenue',
    byDefault: false,
    align: 'end',
    cell: (row, context) => money(row.rangeRevenueMinor, context),
  },
  {
    key: 'range_refunds',
    label: 'Refunds in period',
    group: 'range',
    byDefault: false,
    align: 'end',
    cell: (row, context) => money(row.rangeRefundsMinor, context),
  },
  {
    key: 'customer_type',
    label: 'New or returning',
    group: 'range',
    byDefault: false,
    cell: (row) => (row.customerType === null ? UNDEFINED : row.customerType === 'new' ? 'New' : 'Returning'),
  },
  {
    key: 'registered_at',
    label: 'Registered',
    group: 'record',
    field: 'registeredAt',
    sort: 'registered_at',
    byDefault: false,
    cell: figure('registeredAt', (row) => row.registeredAt, day),
  },
  {
    key: 'opt_ins',
    label: 'Opt-ins',
    group: 'record',
    field: 'consent',
    byDefault: false,
    cell: figure('consent', (row) => row.consent, (consent) => {
      const granted = (['email', 'push', 'analytics', 'advertising'] as const).filter((category) => consent[category] === 'granted')
      return granted.length > 0 ? granted.map((category) => category.charAt(0).toUpperCase() + category.slice(1)).join(', ') : 'None granted'
    }),
  },
  {
    key: 'segments',
    label: 'Segments',
    group: 'record',
    field: 'segments',
    byDefault: false,
    // Always null until segments exist: the cell says why.
    cell: (row, context) => missing(row, 'segments', context),
  },
]

export const COLUMN_KEYS: readonly string[] = CUSTOMER_COLUMNS.map((column) => column.key)
export const DEFAULT_COLUMN_KEYS: readonly string[] = CUSTOMER_COLUMNS.filter((column) => column.byDefault).map(
  (column) => column.key,
)

/** The customers list's column choice, in this browser (`../list/columnChoice`). */
export const COLUMN_CHOICE = {
  storageKey: 'ci.analytics.customers.columns',
  keys: COLUMN_KEYS,
  defaults: DEFAULT_COLUMN_KEYS,
}
